/**
 * API Performance Monitoring Utility
 * Tracks API call performance, success/failure rates, and provides alerting
 */

import { PERFORMANCE_THRESHOLDS } from './performance';

// Store API metrics
const apiMetrics = new Map();
const apiCallbacks = new Set();

// Configuration
const MAX_METRICS_PER_API = 100;
const SLOW_API_THRESHOLD = PERFORMANCE_THRESHOLDS.API_CALL;

/**
 * Subscribe to API metric updates
 * @param {Function} callback - Callback function to receive API metrics
 * @returns {Function} Unsubscribe function
 */
export const subscribeToApiMetrics = (callback) => {
	apiCallbacks.add(callback);
	return () => {
		apiCallbacks.delete(callback);
	};
};

/**
 * Notify all subscribers of a new API metric
 * @param {Object} metric - API metric object
 */
const notifySubscribers = (metric) => {
	apiCallbacks.forEach((callback) => {
		try {
			callback(metric);
		} catch (error) {
			console.error('[apiMonitoring][notifySubscribers] - Error in callback:', error);
		}
	});
};

/**
 * Record API metric
 * @param {string} apiName - Name/identifier of the API call
 * @param {Object} metric - Metric data
 */
const recordApiMetric = (apiName, metric) => {
	if (!apiMetrics.has(apiName)) {
		apiMetrics.set(apiName, []);
	}

	const metrics = apiMetrics.get(apiName);
	metrics.push({
		...metric,
		timestamp: Date.now(),
	});

	// Keep only last N metrics per API
	if (metrics.length > MAX_METRICS_PER_API) {
		metrics.shift();
	}

	// Notify subscribers
	notifySubscribers({ apiName, ...metric });

	// Send alert for slow API calls
	if (metric.duration > SLOW_API_THRESHOLD) {
		sendPerformanceAlert(apiName, metric);
	}
};

/**
 * Send performance alert for slow API calls
 * @param {string} apiName - API name
 * @param {Object} metric - Metric data
 */
const sendPerformanceAlert = (apiName, metric) => {
	if (process.env.NODE_ENV === 'development') {
		console.warn(
			`[apiMonitoring][${apiName}] - Slow API call detected: ${metric.duration.toFixed(2)}ms`
		);
	}

	// In production, you could send this to an error tracking service like Sentry
	if (typeof window !== 'undefined' && window.Sentry) {
		window.Sentry.captureMessage(`Slow API call: ${apiName}`, {
			level: 'warning',
			tags: {
				api: apiName,
				performance: 'slow',
			},
			extra: {
				duration: metric.duration,
				success: metric.success,
				error: metric.error,
			},
		});
	}
};

/**
 * Monitor an API call and track its performance
 * @param {string} apiName - Name/identifier of the API call
 * @param {Function} apiCall - Async function that performs the API call
 * @param {Object} options - Monitoring options
 * @param {Object} options.metadata - Additional metadata to include
 * @returns {Promise<*>} Result of the API call
 */
export const monitorApiCall = async (apiName, apiCall, options = {}) => {
	const startTime = performance.now();
	const requestId = `${apiName}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
	let error = null;
	let duration = 0;

	try {
		const result = await apiCall();
		const endTime = performance.now();
		duration = endTime - startTime;

		// Record successful API call
		recordApiMetric(apiName, {
			success: true,
			duration,
			timestamp: Date.now(),
			requestId,
			metadata: options.metadata || {},
		});

		return result;
	} catch (err) {
		const endTime = performance.now();
		duration = endTime - startTime;
		error = {
			message: err.message,
			stack: err.stack,
			name: err.name,
		};

		// Record failed API call
		recordApiMetric(apiName, {
			success: false,
			duration,
			error: error.message,
			errorType: error.name,
			timestamp: Date.now(),
			requestId,
			metadata: options.metadata || {},
		});

		throw err;
	}
};

/**
 * Get metrics for a specific API
 * @param {string} apiName - API name
 * @returns {Array} Array of metrics for the API
 */
export const getApiMetrics = (apiName) => {
	return apiMetrics.get(apiName) || [];
};

/**
 * Get all API metrics
 * @returns {Map} Map of all API metrics
 */
export const getAllApiMetrics = () => {
	return apiMetrics;
};

/**
 * Get API performance summary
 * @param {string} apiName - Optional API name filter
 * @returns {Object} Performance summary
 */
export const getApiPerformanceSummary = (apiName = null) => {
	if (apiName) {
		const metrics = apiMetrics.get(apiName) || [];
		return calculateSummary(apiName, metrics);
	}

	// Return summary for all APIs
	const summary = {};
	apiMetrics.forEach((metrics, name) => {
		summary[name] = calculateSummary(name, metrics);
	});

	return summary;
};

/**
 * Calculate performance summary for an API
 * @param {string} apiName - API name
 * @param {Array} metrics - Array of metrics
 * @returns {Object} Summary object
 */
const calculateSummary = (apiName, metrics) => {
	if (metrics.length === 0) {
		return {
			apiName,
			totalCalls: 0,
			successCount: 0,
			failureCount: 0,
			successRate: 0,
			averageDuration: 0,
			minDuration: 0,
			maxDuration: 0,
			p95Duration: 0,
			p99Duration: 0,
			slowCallCount: 0,
		};
	}

	const durations = metrics.map((m) => m.duration);
	const successful = metrics.filter((m) => m.success);
	const failed = metrics.filter((m) => !m.success);
	const slowCalls = metrics.filter((m) => m.duration > SLOW_API_THRESHOLD);

	// Calculate percentiles
	const sortedDurations = [...durations].sort((a, b) => a - b);
	const p95Index = Math.floor(sortedDurations.length * 0.95);
	const p99Index = Math.floor(sortedDurations.length * 0.99);

	return {
		apiName,
		totalCalls: metrics.length,
		successCount: successful.length,
		failureCount: failed.length,
		successRate: (successful.length / metrics.length) * 100,
		averageDuration:
			durations.reduce((a, b) => a + b, 0) / durations.length,
		minDuration: Math.min(...durations),
		maxDuration: Math.max(...durations),
		p95Duration: sortedDurations[p95Index] || 0,
		p99Duration: sortedDurations[p99Index] || 0,
		slowCallCount: slowCalls.length,
		slowCallRate: (slowCalls.length / metrics.length) * 100,
		lastCallTime: metrics[metrics.length - 1]?.timestamp || 0,
		lastError: failed.length > 0 ? failed[failed.length - 1]?.error : null,
	};
};

/**
 * Clear metrics for a specific API or all APIs
 * @param {string} apiName - Optional API name to clear
 */
export const clearApiMetrics = (apiName = null) => {
	if (apiName) {
		apiMetrics.delete(apiName);
	} else {
		apiMetrics.clear();
	}
};

/**
 * Create a wrapper function for an API call
 * @param {string} apiName - API name
 * @param {Function} apiFunction - Original API function
 * @returns {Function} Wrapped function with monitoring
 */
export const wrapApiFunction = (apiName, apiFunction) => {
	return async (...args) => {
		return monitorApiCall(apiName, () => apiFunction(...args), {
			metadata: {
				args: args.length,
				functionName: apiFunction.name || 'anonymous',
			},
		});
	};
};

/**
 * Batch monitor multiple API calls
 * @param {Array} apiCalls - Array of { name, call } objects
 * @returns {Promise<Array>} Array of results
 */
export const monitorBatchApiCalls = async (apiCalls) => {
	const results = await Promise.allSettled(
		apiCalls.map(({ name, call }) => monitorApiCall(name, call))
	);

	return results.map((result, index) => {
		if (result.status === 'fulfilled') {
			return { success: true, data: result.value, apiName: apiCalls[index].name };
		} else {
			return {
				success: false,
				error: result.reason,
				apiName: apiCalls[index].name,
			};
		}
	});
};

